"use strict";
const remoteContainerRegistry = /* @__PURE__ */ new Map();
const REMOTE_SCRIPT_LOAD_TIMEOUT_MS = 8e3;
const FEDERATION_MAX_ATTEMPTS = 3;
const FEDERATION_RETRY_BASE_DELAY_MS = 300;
function getRemoteContainerKey(scope, remoteEntry) {
  return `${scope}|${remoteEntry}`;
}
function isBrowser() {
  return typeof window !== "undefined";
}
function getBuildEpoch() {
  const hostGlobal = globalThis;
  return hostGlobal.__CAPRX_BUILD_EPOCH__;
}
function emitModuleLoadEvent(detail) {
  if (!isBrowser()) return;
  globalThis.dispatchEvent(
    new CustomEvent("caprx:shell-module-load", {
      detail
    })
  );
}
function emitModuleDiagnosticEvent(detail) {
  if (!isBrowser()) return;
  globalThis.dispatchEvent(
    new CustomEvent("caprx:shell-module-diagnostic", {
      detail: {
        ...detail,
        buildEpoch: getBuildEpoch(),
        timestamp: (/* @__PURE__ */ new Date()).toISOString()
      }
    })
  );
}
function validateFederationDescriptor(moduleId, federation) {
  var _a, _b;
  const remoteEntry = (_a = federation.remoteEntry) == null ? void 0 : _a.trim();
  const scope = (_b = federation.scope) == null ? void 0 : _b.trim();
  if (!remoteEntry) {
    throw new Error(`Module [${moduleId}] federation.remoteEntry must be a non-empty string.`);
  }
  if (!scope) {
    throw new Error(`Module [${moduleId}] federation.scope must be a non-empty string.`);
  }
}
async function sleep(delayMs) {
  await new Promise((resolve) => {
    setTimeout(resolve, delayMs);
  });
}
function getRetryDelayMs(attempt) {
  const exponent = Math.max(attempt - 1, 0);
  return FEDERATION_RETRY_BASE_DELAY_MS * 2 ** exponent;
}
function getHostWindow() {
  return window;
}
function remoteEntryScriptSelector(remoteEntry) {
  var _a;
  const escape = (_a = globalThis.CSS) == null ? void 0 : _a.escape;
  const value = escape ? escape(remoteEntry) : remoteEntry.replace(/["\\]/g, "\\$&");
  return `script[data-caprx-remote-entry="${value}"]`;
}
function isRetryableFederationError(error) {
  if (!(error instanceof Error)) return true;
  const deterministic = ["is not available after loading", "does not contain", "is not a valid module namespace"];
  return !deterministic.some((fragment) => error.message.includes(fragment));
}
function getManifestFromRemoteModule(candidate) {
  if (typeof candidate !== "object" || candidate === null) {
    throw new Error("Loaded module is not a valid module namespace object.");
  }
  const manifestContainer = candidate;
  const manifest = manifestContainer.default;
  if (!manifest || typeof manifest !== "object") {
    throw new Error("Loaded module does not contain a default manifest export.");
  }
  return manifest;
}
function ensureRemoteScriptLoaded(remoteEntry, scope) {
  return new Promise((resolve, reject) => {
    let timeout = null;
    const cleanup = (script2, onLoad, onError) => {
      script2.removeEventListener("load", onLoad);
      script2.removeEventListener("error", onError);
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
    };
    const attachHandlers = (script2) => {
      const onLoad = () => {
        script2.dataset.caprxLoadState = "loaded";
        script2.dataset.loaded = "true";
        cleanup(script2, onLoad, onError);
        resolve();
      };
      const onError = () => {
        script2.dataset.caprxLoadState = "failed";
        cleanup(script2, onLoad, onError);
        script2.remove();
        reject(new Error(`Unable to load federation remote script [${remoteEntry}].`));
      };
      script2.addEventListener("load", onLoad, { once: true });
      script2.addEventListener("error", onError, { once: true });
      timeout = setTimeout(() => {
        script2.dataset.caprxLoadState = "failed";
        cleanup(script2, onLoad, onError);
        script2.remove();
        reject(new Error(`Timed out while loading federation remote script [${remoteEntry}] after ${REMOTE_SCRIPT_LOAD_TIMEOUT_MS}ms.`));
      }, REMOTE_SCRIPT_LOAD_TIMEOUT_MS);
    };
    const existingScript = document.querySelector(remoteEntryScriptSelector(remoteEntry));
    if (existingScript) {
      existingScript.dataset.caprxScope = scope;
      if (existingScript.dataset.loaded === "true" || existingScript.dataset.caprxLoadState === "loaded") {
        resolve();
        return;
      }
      if (existingScript.dataset.caprxLoadState === "failed") {
        existingScript.remove();
      } else {
        attachHandlers(existingScript);
        return;
      }
    }
    const script = document.createElement("script");
    script.src = remoteEntry;
    script.async = true;
    script.type = "text/javascript";
    script.dataset.caprxRemoteEntry = remoteEntry;
    script.dataset.caprxScope = scope;
    script.dataset.caprxLoadState = "loading";
    script.crossOrigin = "anonymous";
    attachHandlers(script);
    document.head.appendChild(script);
  });
}
async function getOrCreateFederationContainer(remoteEntry, scope) {
  const key = getRemoteContainerKey(scope, remoteEntry);
  const existing = remoteContainerRegistry.get(key);
  if (existing) return existing;
  const promise = (async () => {
    await ensureRemoteScriptLoaded(remoteEntry, scope);
    const hostWindow = getHostWindow();
    const container = hostWindow[scope];
    if (!container || typeof container.get !== "function" || typeof container.init !== "function") {
      throw new Error(`Remote scope [${scope}] is not available after loading [${remoteEntry}].`);
    }
    await __webpack_init_sharing__("default");
    await container.init(__webpack_share_scopes__.default);
    return container;
  })();
  remoteContainerRegistry.set(key, promise);
  try {
    return await promise;
  } catch (err) {
    remoteContainerRegistry.delete(key);
    throw err;
  }
}
export function clearFederationRuntimeCache(modules) {
  if (!isBrowser()) {
    remoteContainerRegistry.clear();
    return;
  }
  if (!modules) {
    const hostWindow2 = getHostWindow();
    const scopesToDelete = /* @__PURE__ */ new Set();
    for (const key of remoteContainerRegistry.keys()) {
      const [scope] = key.split("|");
      if (scope) {
        scopesToDelete.add(scope);
      }
    }
    document.querySelectorAll("script[data-caprx-remote-entry]").forEach((scriptNode) => {
      const script = scriptNode;
      const scriptScope = script.dataset.caprxScope;
      if (scriptScope) {
        scopesToDelete.add(scriptScope);
      }
    });
    for (const scope of scopesToDelete) {
      if (Object.prototype.hasOwnProperty.call(hostWindow2, scope)) {
        delete hostWindow2[scope];
      }
    }
    remoteContainerRegistry.clear();
    document.querySelectorAll("script[data-caprx-remote-entry]").forEach((script) => script.remove());
    return;
  }
  const hostWindow = getHostWindow();
  for (const descriptor of modules) {
    const federation = descriptor.federation;
    if (!federation) {
      continue;
    }
    const key = getRemoteContainerKey(federation.scope, federation.remoteEntry);
    remoteContainerRegistry.delete(key);
    const script = document.querySelector(remoteEntryScriptSelector(federation.remoteEntry));
    if (script) {
      script.remove();
    }
    if (Object.prototype.hasOwnProperty.call(hostWindow, federation.scope)) {
      delete hostWindow[federation.scope];
    }
  }
}
async function loadManifestFromFederation(context, moduleId, config) {
  if (!isBrowser()) {
    throw new Error("Federation loading is only available in browser environments.");
  }
  const exposedModule = config.exposedModule || "./manifest";
  emitModuleDiagnosticEvent({
    moduleId,
    context,
    stage: "remote-bootstrap",
    status: "start",
    source: "federation"
  });
  const container = await getOrCreateFederationContainer(config.remoteEntry, config.scope);
  emitModuleDiagnosticEvent({
    moduleId,
    context,
    stage: "remote-bootstrap",
    status: "success",
    source: "federation"
  });
  emitModuleDiagnosticEvent({
    moduleId,
    context,
    stage: "container-get",
    status: "start",
    source: "federation"
  });
  const factory = await container.get(exposedModule);
  emitModuleDiagnosticEvent({
    moduleId,
    context,
    stage: "container-get",
    status: "success",
    source: "federation"
  });
  const loaded = factory();
  return getManifestFromRemoteModule(loaded);
}
async function loadManifestWithRetry(context, moduleId, descriptor, federation) {
  let previousError;
  for (let attempt = 1; attempt <= FEDERATION_MAX_ATTEMPTS; attempt++) {
    emitModuleDiagnosticEvent({
      moduleId,
      context,
      stage: "load-manifest",
      status: "start",
      source: "federation",
      attempt
    });
    try {
      const manifest = await loadManifestFromFederation(context, moduleId, federation);
      emitModuleDiagnosticEvent({
        moduleId,
        context,
        stage: "manifest-resolve",
        status: "success",
        source: "federation",
        attempt
      });
      return manifest;
    } catch (error) {
      previousError = error;
      const message = error instanceof Error ? error.message : String(error);
      emitModuleDiagnosticEvent({
        moduleId,
        context,
        stage: "load-manifest",
        status: "failure",
        source: "federation",
        attempt,
        message
      });
      clearFederationRuntimeCache([descriptor]);
      if (attempt < FEDERATION_MAX_ATTEMPTS && isRetryableFederationError(error)) {
        const delayMs = getRetryDelayMs(attempt);
        emitModuleDiagnosticEvent({
          moduleId,
          context,
          stage: "load-manifest",
          status: "retry",
          source: "federation",
          attempt,
          message: `Retrying manifest load in ${delayMs}ms.`
        });
        await sleep(delayMs);
        continue;
      }
      throw previousError;
    }
  }
  if (previousError instanceof Error) {
    throw previousError;
  }
  throw new Error(`Module [${moduleId}] could not be loaded from federation.`);
}
export async function loadModuleManifest(context, descriptor) {
  const { id: moduleId, federation } = descriptor;
  if (!federation) {
    throw new Error(`Module [${moduleId}] is missing federation configuration.`);
  }
  emitModuleDiagnosticEvent({
    moduleId,
    context,
    stage: "descriptor-validate",
    status: "start",
    source: "federation"
  });
  validateFederationDescriptor(moduleId, federation);
  emitModuleDiagnosticEvent({
    moduleId,
    context,
    stage: "descriptor-validate",
    status: "success",
    source: "federation"
  });
  emitModuleLoadEvent({ moduleId, context, source: "federation", phase: "start" });
  try {
    const module = await loadManifestWithRetry(context, moduleId, descriptor, federation);
    emitModuleLoadEvent({ moduleId, context, source: "federation", phase: "success" });
    return { source: "federation", module };
  } catch (err) {
    emitModuleLoadEvent({
      moduleId,
      context,
      source: "federation",
      phase: "failure",
      message: err instanceof Error ? err.message : String(err)
    });
    throw err;
  }
}
